import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Activity, Database, Mail, Clock, ShieldAlert, RefreshCw, Send, AlertTriangle, CheckCircle2 } from "lucide-react";

const Gauge = ({ label, value, warn, max, unit = "", testId }) => {
  const pct = Math.min(100, Math.round((value / max) * 100));
  const color = value >= max ? "bg-[#B23B3B]" : value >= warn ? "bg-[#C98A2B]" : "bg-[#7B8A72]";
  return (
    <div data-testid={testId}>
      <div className="flex justify-between text-xs text-[#7C7064] mb-1"><span>{label}</span><span className="text-[#2C2621]">{value}{unit} / {max}{unit}</span></div>
      <div className="h-2 bg-[#F0E8DA]"><div className={`h-2 ${color}`} style={{ width: `${pct}%` }}/></div>
    </div>
  );
};

const Card = ({ icon: Icon, title, children, testId }) => (
  <section data-testid={testId} className="bg-white border border-[#E8DCC8] p-5 space-y-3">
    <h2 className="flex items-center gap-2 font-serif text-lg"><Icon size={16} className="text-[#A3876A]"/>{title}</h2>
    {children}
  </section>
);

export default function AdminSystem() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/system/status").then(r => setD(r.data)).catch(() => toast.error("Impossible de charger l'état du site"));
  useEffect(() => { load(); }, []);

  async function run(path, label) {
    setBusy(path);
    try { const r = await api.post(path); toast.success(label(r.data)); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(""); }
  }

  if (!d) return <p className="text-sm text-[#7C7064]">Chargement…</p>;
  const { emails: e, storage: s, traffic: t, crons, alerts } = d;
  const fmt = (iso) => iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—";

  return (
    <div data-testid="admin-system-page" className="space-y-8 max-w-5xl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-hand text-xl text-[#A3876A] mb-1">Veille du site</p>
          <h1 className="font-serif text-3xl">Système & alertes</h1>
          <p className="text-sm text-[#7C7064] mt-1">Vérification automatique toutes les 15 min. Aucun email de veille : tout se consulte ici.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="system-check-btn" disabled={!!busy} onClick={() => run("/admin/system/check", r => `${r.active} alerte(s) active(s)`)} className="btn-outline !py-2 inline-flex items-center gap-1.5"><RefreshCw size={13}/> Vérifier maintenant</button>
          {e.failures_pending > 0 && <button data-testid="system-retry-btn" disabled={!!busy} onClick={() => run("/admin/system/retry-failed-emails", r => `${r.resent} renvoyé(s) · ${r.still_failed} toujours en échec`)} className="btn-primary !py-2 inline-flex items-center gap-1.5"><Send size={13}/> Renvoyer {e.failures_pending} email(s) en échec</button>}
        </div>
      </div>

      <section data-testid="system-alerts" className={`border p-5 ${alerts.length ? "bg-[#FBF3EC] border-[#E3C6A8]" : "bg-[#F1F4EE] border-[#CBD5C3]"}`}>
        <h2 className="flex items-center gap-2 font-serif text-lg mb-3">{alerts.length ? <AlertTriangle size={16} className="text-[#C98A2B]"/> : <CheckCircle2 size={16} className="text-[#7B8A72]"/>}
          {alerts.length ? `${alerts.length} alerte(s) active(s)` : "Tout va bien — aucune action nécessaire"}</h2>
        <ul className="space-y-3">
          {alerts.map(a => (
            <li key={a.key} data-testid={`alert-${a.key.split(":")[0]}`} className="bg-white/70 border border-[#E8DCC8] p-3">
              <p className={`text-[10px] uppercase tracking-wider ${a.level === "critical" ? "text-[#B23B3B]" : "text-[#C98A2B]"}`}>{a.level === "critical" ? "Critique" : "Attention"}</p>
              <p className="font-medium text-sm">{a.title}</p>
              <p className="text-sm text-[#4A4036]">{a.message}</p>
              <p className="text-xs text-[#7C7064] mt-1">→ {a.action}</p>
            </li>
          ))}
        </ul>
      </section>

      <div className="grid md:grid-cols-2 gap-5">
        <Card icon={Mail} title="Emails (plan Resend gratuit)" testId="system-emails">
          <Gauge label="Aujourd'hui" value={e.today} warn={e.day_warn} max={e.day_max} testId="gauge-emails-day"/>
          <Gauge label="Ce mois-ci" value={e.month} warn={e.month_warn} max={e.month_max} testId="gauge-emails-month"/>
          <p className="text-xs text-[#7C7064]">Échecs aujourd'hui : <span data-testid="emails-failures">{e.failures_today}</span> · en attente de renvoi : {e.failures_pending}. Alerte email à {e.day_warn}/jour et {e.month_warn}/mois.</p>
        </Card>
        <Card icon={Database} title="Stockage base de données" testId="system-storage">
          {s.error ? <p className="text-xs text-[#B23B3B]">{s.error}</p> : <>
            <Gauge label="Espace utilisé" value={s.storage_mb} warn={s.warn_mb} max={s.crit_mb} unit=" Mo" testId="gauge-storage"/>
            <p className="text-xs text-[#7C7064]">{s.objects.toLocaleString("fr-FR")} enregistrements · données brutes {s.data_mb} Mo.</p></>}
        </Card>
        <Card icon={Activity} title="Vitesse du site (15 dernières min)" testId="system-traffic">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div><p className="text-2xl font-serif" data-testid="traffic-count">{t.count}</p><p className="text-[11px] text-[#7C7064]">Requêtes</p></div>
            <div><p className={`text-2xl font-serif ${t.p95_s >= 2 ? "text-[#C98A2B]" : ""}`} data-testid="traffic-p95">{t.p95_s}s</p><p className="text-[11px] text-[#7C7064]">Temps max (95 %)</p></div>
            <div><p className={`text-2xl font-serif ${t.errors_5xx ? "text-[#B23B3B]" : ""}`} data-testid="traffic-errors">{t.errors_5xx}</p><p className="text-[11px] text-[#7C7064]">Erreurs serveur</p></div>
          </div>
          <p className="text-xs text-[#7C7064]">Alerte si le site dépasse 2 s pour 95 % des requêtes → passer à un palier supérieur (Manage Publishes → Resources).</p>
        </Card>
        <Card icon={Clock} title="Tâches automatiques" testId="system-crons">
          <ul className="text-sm space-y-1">
            {crons.length === 0 && <li className="text-xs text-[#7C7064] italic">Aucune exécution enregistrée pour l'instant (le suivi commence maintenant).</li>}
            {crons.map(c => <li key={c.name} className="flex justify-between"><span>{c.name}</span><span className={c.late ? "text-[#B23B3B]" : "text-[#7C7064]"}>{fmt(c.last_run_at)}{c.late && " · en retard"}</span></li>)}
          </ul>
          <p className="text-xs text-[#7C7064] flex items-center gap-1"><ShieldAlert size={12}/> Stripe : {d.stripe_enabled ? "configuré" : "non configuré"} · dernier paiement confirmé : {fmt(d.last_paid_checkout)}</p>
        </Card>
      </div>

    </div>
  );
}
